import { IFrame, Client as StompClient, closeEventCallbackType, frameCallbackType } from '@stomp/stompjs';
import { logger } from 'common/services';
import EventEmitter from 'events';
import type Keycloak from 'keycloak-js';
import { UrlNames } from 'modules/app/constants';
import type ConfigService from 'modules/app/services/ConfigService';
import { updateKeycloakToken } from 'modules/keycloak';

import { SocketRoomPrefixType, SocketSubscribeParams } from './types';
import { getWebsocket } from './utils';

export const SocketEvents = {
  connected: 'connected',
  disconnected: 'disconnected',
  reconnected: 'reconnected',
  newMessage: 'newMessage',
  channelSubscribed: 'channelSubscribed',
  channelUnsubscribed: 'channelUnsubscribed',
  unauthenticated: 'unauthenticated',
  reconnecting: 'reconnecting',
  connectionError: 'connectionError',
};

interface WebSocketServiceAttributes {
  configService: ConfigService;
  keycloakService: Keycloak;
}

interface WebSocketChannelSubscription {
  id: string;
  unsubscribe: () => void;
}

export interface WebSocketConnectionState {
  isConnected: boolean;
  isReconnecting: boolean;
}

export interface SocketDisconnected {
  closedGracefully: boolean;
}

export interface SocketPayload {
  action: string;
  params: string;
}

export interface SocketMessage {
  path: string;
  payload: SocketPayload | Omit<SocketPayload, 'action'> | SocketPayload[] | undefined;
}

export class Socket extends EventEmitter {
  readonly brokerURL: string;
  private readonly keycloak: Keycloak;
  private readonly client: StompClient | null;
  private initialized: boolean;
  private readonly subscriptions = new Map<
    string,
    { subscription: WebSocketChannelSubscription; actionPrefix: SocketRoomPrefixType }
  >();
  private awaitingSubscriptions: { actionPrefix: SocketRoomPrefixType; channelName: string }[] = [];
  private readonly awaitingMessages: { entrypoint: string; params: object; plainBody: boolean }[] = [];
  private reconnecting: boolean;

  constructor({ keycloakService, configService }: WebSocketServiceAttributes) {
    super();
    this.brokerURL = `${configService.getUrl(UrlNames.Base)}community`;
    this.keycloak = keycloakService;
    this.client = null;
    this.initialized = false;
    this.reconnecting = false;

    const client = new StompClient({
      brokerURL: this.brokerURL,
      beforeConnect: this.beforeConnect,
      onConnect: this.connectCallback,
      onDisconnect: this.handleDisconnect,
      onWebSocketClose: this.handleClose,
      reconnectDelay: 2_000,
      heartbeatIncoming: 25_000,
      heartbeatOutgoing: 25_000,
      webSocketFactory: () => getWebsocket(this.brokerURL),
      onStompError: (frame) => {
        logger.error('WebSocket STOMP protocol error', { wsBrokerURL: this.brokerURL, frame });
      },
      onWebSocketError: (error) => {
        logger.error('WebSocket error', { wsBrokerURL: this.brokerURL }, error);
      },
      debug: (message) => {
        logger.debug('WebSocket STOMP debug', { wsBrokerURL: this.brokerURL, message });
      },
    });
    this.client = client;

    this.keycloak.onAuthSuccess = () => {
      client.activate();
    };
  }

  private readonly beforeConnect = async () => {
    if (!this.client) return;

    const connectionType = this.initialized ? 'reconnection' : 'initial connection';
    if (this.initialized) {
      this.reconnecting = true;
    }

    logger.info('WebSocket connection attempt', {
      wsBrokerURL: this.brokerURL,
      wsConnectionType: connectionType,
      wsAuthenticated: this.keycloak.authenticated,
    });

    try {
      if (this.keycloak.authenticated) {
        await updateKeycloakToken(this.keycloak);
        logger.info('WebSocket token updated', { wsBrokerURL: this.brokerURL });
      }
      if (this.keycloak.token) {
        this.client.connectHeaders = { token: this.keycloak.token };
      }
    } catch (error) {
      logger.warn('WebSocket token update failed', { wsBrokerURL: this.brokerURL, error });
    }
  };

  private readonly handleChannelSubscription = (actionPrefix: SocketRoomPrefixType, channelName: string) => {
    if (this.client?.connected) {
      this.subscribeToChannel(this.client, actionPrefix, channelName);
    } else if (!this.awaitingSubscriptions.some((s) => s.channelName === channelName)) {
      this.awaitingSubscriptions.push({ actionPrefix, channelName });
    }
  };

  private readonly subscribeToChannel = (
    client: StompClient,
    actionPrefix: SocketRoomPrefixType,
    channelName: string
  ) => {
    const subscription = client.subscribe(channelName, this.messageHandler(actionPrefix));
    this.subscriptions.set(channelName, { actionPrefix, subscription });
    logger.info('WebSocket channel subscribed', {
      wsBrokerURL: this.brokerURL,
      wsChannel: channelName,
      wsActionPrefix: actionPrefix,
    });
    this.emit(SocketEvents.channelSubscribed, channelName);
  };

  private readonly messageHandler =
    (actionPrefix: string): ((messageOutput: { body: string }) => void) =>
    (messageOutput): void => {
      const parsedOutput = JSON.parse(messageOutput.body);
      this.emit(SocketEvents.newMessage, {
        path: `${actionPrefix}:${parsedOutput.action}`,
        payload: parsedOutput.params ?? parsedOutput.content ?? parsedOutput.data,
      } as SocketMessage);
    };

  private readonly sendMessage = (entrypoint: string, params: object = {}, plainBody = false) => {
    this.client?.publish({
      destination: encodeURI(entrypoint),
      headers: {},
      body: JSON.stringify(plainBody ? params : { params }),
    });
  };

  private readonly connectCallback = (frame: IFrame) => {
    if (this.initialized) {
      this.emit(SocketEvents.reconnected);
      logger.info('WebSocket reconnected to server', { wsBrokerURL: this.brokerURL, frame });
    } else {
      logger.info('WebSocket connected', { wsBrokerURL: this.brokerURL });
      this.emit(SocketEvents.connected);
    }

    for (let i = this.awaitingSubscriptions.length - 1; i >= 0; i--) {
      if (!this.client) return;
      const { actionPrefix, channelName } = this.awaitingSubscriptions[i];
      this.subscribeToChannel(this.client, actionPrefix, channelName);
      this.awaitingSubscriptions.splice(i, 1);
    }

    for (let i = this.awaitingMessages.length - 1; i >= 0; i--) {
      if (!this.client) return;
      const { entrypoint, params, plainBody } = this.awaitingMessages[i];
      this.sendMessage(entrypoint, params, plainBody);
      this.awaitingMessages.splice(i, 1);
    }

    this.initialized = true;
    this.reconnecting = false;
  };

  send(entrypoint: string, params: object = {}, plainBody = false): void {
    if (this.client?.connected) {
      this.sendMessage(entrypoint, params, plainBody);
    } else {
      this.awaitingMessages.push({ entrypoint, params, plainBody });
    }
  }

  handleDisconnect: frameCallbackType = (frame) => {
    logger.debug('WebSocket disconnected (STOMP)', { wsBrokerURL: this.brokerURL, frame });

    this.initialized = false;

    this.emit(SocketEvents.disconnected);
  };

  handleClose: closeEventCallbackType = (closeEvent: CloseEvent) => {
    if (!this.initialized || this.reconnecting) {
      return;
    }

    logger.info('WebSocket connection closed', { wsBrokerURL: this.brokerURL, closeEvent });

    this.emit(SocketEvents.connectionError);

    this.subscriptions.forEach(({ actionPrefix }, channelName) => {
      this.awaitingSubscriptions.push({ actionPrefix, channelName });
      this.subscriptions.delete(channelName);
      this.emit(SocketEvents.channelUnsubscribed, channelName);
    });
  };

  subscribe({ actionPrefix, privateChannel, publicChannel }: SocketSubscribeParams): void {
    if (!this.subscriptions.has(publicChannel)) {
      this.handleChannelSubscription(actionPrefix, publicChannel);
    }
    if (privateChannel && !this.subscriptions.has(privateChannel)) {
      this.handleChannelSubscription(actionPrefix, privateChannel);
    }
  }

  unsubscribe({ publicChannel, privateChannel }: SocketSubscribeParams): void {
    [publicChannel, privateChannel].forEach((channelName) => {
      if (!channelName) return;

      const subscriptionData = this.subscriptions.get(channelName);
      if (!subscriptionData) {
        return;
      }
      subscriptionData.subscription.unsubscribe();
      this.subscriptions.delete(channelName);
      logger.info('WebSocket channel unsubscribed', {
        wsBrokerURL: this.brokerURL,
        wsChannel: channelName,
      });
      this.emit(SocketEvents.channelUnsubscribed, channelName);
    });

    const isPublicOrPrivateChannel = (channelName: string) => [publicChannel, privateChannel].includes(channelName);
    if (this.awaitingSubscriptions.some(({ channelName }) => isPublicOrPrivateChannel(channelName))) {
      this.awaitingSubscriptions = this.awaitingSubscriptions.filter(
        ({ channelName }) => !isPublicOrPrivateChannel(channelName)
      );
    }
  }

  openPublicConnection = () => {
    this.client?.activate();
  };

  closePublicConnection = () => {
    this.client?.deactivate();
  };
}
